import { Suspense, lazy, useMemo, useState } from 'react';

import { SessionContext } from '@contexts/session-context';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { Feature } from '@enums/feature.enum';
import { StrollrEventType } from '@enums/strollr-event-type.enum';
import { UseBaseHooks } from '@hooks/use-base.hooks';
import { Page } from '@models/page';
import { UserProjectMapping } from '@models/user-project-mapping.model';
import { NotificationBanner } from '@modules/notification-banner';
import SalesForceRoutes from '@modules/salesforce/connections/routes';
import { LoadDebuggerPage } from '@modules/test-development/pages/mobile-debug/load-debugger-page';
import PrimaryNavigation from '@navigation/App';
import Overlay from '@overlay/App';
import { useGlobalState } from '@providers/global-context-provider';
import StrollEventService from '@services/strollr-event.service';
import UserProjectMappingService from '@services/user-project-mapping.service';
import BulkActionLoader from '@shared/components/bulk-action-loader';
import ErrorBoundary from '@shared/components/error-boundary';
import Fallback from '@shared/components/fallback';
import FreshChat from '@shared/components/freshchat';
import PageLoading from '@shared/components/loading';
import RouteGuard from '@shared/components/route-guard';
import { isPreview, logErrorToSentry } from '@utils/common';
import { useNavBarPath } from '@utils/nav-bar';
import classNames from 'classnames';
import { useContext, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import NotFoundComponent from './error';
import TrailExpired from './trail-expired';
import UserLimitExceeded from './user-limit-exceeded';
import LinkExpiredComponent from '@routes/error/link-expired';
const LazyTestDevelopmentRoutes = lazy(
  () => import('@modules/test-development/routes'),
);
const LazyAddonRoutes = lazy(() => import('./addon-routes'));
const LazyAIJoryney = lazy(() => import('@modules/ai-journey'));
const LazyProjectRoutes = lazy(() => import('@modules/project/routes'));
const LazySettingRoutes = lazy(() => import('@modules/settings/routes'));

const LazyAgentRoutes = lazy(() => import('@modules/agent/routes'));

const LazyDashboard = lazy(() => import('@modules/dashboard/index'));
const LazyNoAccess = lazy(() => import('@shared/components/no-access'));
const LazyTokenExpiredComponent = lazy(
  () => import('@routes/error/token-expired'),
);
const LazyDashboardTemplate = lazy(() => import('./dashboard'));
const LazySavePointsListView = lazy(() => import('@modules/save-points'));
const LazyPreview = lazy(() => import('@modules/preview'));
const LazyListView = lazy(
  () => import('@modules/test-development/pages/cases/list-view'),
);

const LazyListViewContextProvider = lazy(
  () => import('@modules/test-development/contexts/list-view-context-provider'),
);
const LazyElementListView = lazy(
  () => import('@modules/test-development/pages/elements/list-view'),
);
const LazyElementsFilter = lazy(
  () => import('@modules/test-development/pages/elements/elements-filter'),
);
const LazyTestCaseFilter = lazy(
  () => import('@modules/test-development/pages/cases/test-case-filter'),
);
const LazyTestDataProfileListView = lazy(
  () => import('@modules/test-development/pages/test-data-profiles/list-view'),
);
const LazyDataMapProvider = lazy(
  () => import('@modules/test-development/contexts/data-map-context-provider'),
);
const LazyPreviewContextProvider = lazy(
  () => import('@modules/preview/contexts'),
);

const LazyTestcaseSteps = lazy(
  () => import('@modules/test-development/pages/steps'),
);

const LazyDetailViewAndFormContextProvider = lazy(
  () =>
    import(
      '@modules/test-development/contexts/detail-view-and-form/detail-view-and-form-context-provider'
    ),
);

const LazyTestDataProfileDetailsPage = lazy(
  () =>
    import('@modules/test-development/components/test-data-profiles/details'),
);

export const AppRouter = () => {
  const { session } = useContext(SessionContext);
  const { pathname } = useLocation();
  const { notificationCount, updateNotificationCount } = useGlobalState();
  const isMobileRecorder = pathname.includes('/record');
  const isMobileRecorderIframe = document.location.href.includes('/recording');
  const isSharableTestCase = pathname.includes('td/sharable/');
  const isTokenExpired = pathname.includes('/token-expired');
  const isMobileNativeDebugger = useMemo(() => {
    return pathname.includes('/debugrun');
  }, [pathname]);
  const isReportGenratingXLSPage = pathname?.includes(
    'execution_results/generate_xls',
  );
  const isLinkExpiredPage = pathname?.includes('/link_expired');
  const isPreviewPage = isPreview();

  const { isExpanded } = useNavBarPath();
  const { hasUserPreference, hasFeature } = UseBaseHooks();
  const { userPreference, userProjectIds, setUserProjectIds } = useContext(
    UserPreferenceContext,
  );
  const isBulkActionOngoing = userPreference?.isBulkActionOngoing;
  const bulkActionId = userPreference?.bulkActionId;
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const containerStyle = {
    height: `calc(100% - ${notificationCount * 50}px)`,
    maxHeight: `calc(100% - ${notificationCount * 50}px)`,
  };

  const hasNavigationMenu = !(
    isMobileRecorder ||
    isSharableTestCase ||
    isTokenExpired ||
    isMobileNativeDebugger ||
    isReportGenratingXLSPage ||
    isLinkExpiredPage ||
    isPreviewPage
  );

  useEffect(() => {
    hasUserPreference('migratedToNewUiForcefully') &&
      StrollEventService.track(StrollrEventType.SHOW_NEW_UI_ONBOARDING_POPUP);
  }, [hasUserPreference, hasFeature]);

  useEffect(() => {
    const fetchAllPages = async () => {
      try {
        // Fetch the first page to get totalPages
        setIsFetching(true);
        const firstResponse = await UserProjectMappingService.findAll(
          `?query=userId:${session.user.id}&page=0&size=20`,
        );
        const firstPage: Page<UserProjectMapping> = firstResponse;
        const allProjectIds = firstPage.content.map((item) => item.projectId);
        const totalPages = firstPage.totalPages;

        // Fetch remaining pages in parallel
        const pagePromises = [];
        for (let i = 1; i < totalPages; i++) {
          pagePromises.push(
            UserProjectMappingService.findAll(
              `?query=userId:${session.user.id}&page=${i}`,
            ),
          );
        }

        const remainingResponses = await Promise.all(pagePromises);
        remainingResponses.forEach((response) => {
          const page: Page<UserProjectMapping> = response;
          page.content.forEach((item) => {
            allProjectIds.push(item.projectId);
          });
        });

        setUserProjectIds(allProjectIds);
      } catch (err) {
        logErrorToSentry({ error: err });
      } finally {
        setIsFetching(false);
      }
    };

    fetchAllPages();
  }, [session.user.id, userPreference]);

  const hasDisplayPermission = useMemo(() => {
    return userPreference?.preference?.projectPreference?.projectId &&
      userProjectIds?.length > 0 &&
      !isFetching
      ? userProjectIds.includes(
          userPreference?.preference?.projectPreference.projectId,
        )
      : true;
  }, [
    userProjectIds,
    isFetching,
    session.user.id,
    userPreference?.preference?.projectPreference?.projectId,
  ]);

  useEffect(() => {
    // tsLog activation function
    window['tslog'] =
      window.location.hostname !== 'app.testsigma.com' || session.isSpoofing;
  }, [session]);

  return (
    <>
      <Overlay />
      {!isMobileRecorderIframe && (
        <NotificationBanner setNotificationCount={updateNotificationCount} />
      )}
      {isBulkActionOngoing && bulkActionId && (
        <BulkActionLoader id={bulkActionId} />
      )}
      <div className={'flex'} style={containerStyle}>
        {hasNavigationMenu &&
          Boolean(session?.user?.authType != 'ADMIN_SSO') && <FreshChat />}
        {hasNavigationMenu && (
          <div
            className={classNames(
              isExpanded ? 'w-[220px]' : 'w-[60px]',
              notificationCount ? 'h-calc[100vh-50px]' : 'h-[100vh]',
              'bg-secondary-100 flex transition duration-500 overflow-x-hidden relative',
            )}
          >
            <PrimaryNavigation key='primary-navigator' />
          </div>
        )}
        <div
          className={classNames(
            'h-full overflow-hidden',
            hasNavigationMenu &&
              (isExpanded
                ? 'basis-[calc(100%-220px)]'
                : 'basis-[calc(100%-60px)]'),
            { 'w-full': !hasNavigationMenu },
          )}
        >
          <ErrorBoundary fallback={Fallback}>
            <Routes>
              <Route path='/link_expired' element={<LinkExpiredComponent />} />
              <Route
                path='/'
                element={
                  <Suspense fallback={<PageLoading />}>
                    <LazyDashboardTemplate />
                  </Suspense>
                }
                errorElement={<NotFoundComponent />}
              >
                <Route index element={<Navigate to='/dashboard' />} />
                <Route
                  path='dashboard'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <RouteGuard access={hasDisplayPermission}>
                        {hasDisplayPermission ? <LazyDashboard /> : <></>}
                      </RouteGuard>
                    </Suspense>
                  }
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='ai-journey'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <RouteGuard
                        access={
                          hasFeature(Feature.AGENTIC_AI) ||
                          hasFeature(Feature.GEN_AI)
                        }
                      >
                        <LazyAIJoryney />
                      </RouteGuard>
                    </Suspense>
                  }
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='ai-journey/:versionId/:flowId/:testCaseKey'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <RouteGuard access={hasFeature(Feature.AGENTIC_AI)}>
                        <LazyAIJoryney />
                      </RouteGuard>
                    </Suspense>
                  }
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='load-debugger'
                  element={<LoadDebuggerPage />}
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='td/*'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <RouteGuard access={hasDisplayPermission}>
                        {hasDisplayPermission ? (
                          <LazyTestDevelopmentRoutes />
                        ) : (
                          <></>
                        )}
                      </RouteGuard>
                    </Suspense>
                  }
                  errorElement={<NotFoundComponent />}
                ></Route>
                <Route
                  path='projects/*'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <RouteGuard access={hasDisplayPermission}>
                        {hasDisplayPermission ? <LazyProjectRoutes /> : <></>}
                      </RouteGuard>
                    </Suspense>
                  }
                />
                <Route
                  path='agents/*'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyAgentRoutes />
                    </Suspense>
                  }
                />
                <Route
                  path='salesforce'
                  element={<Outlet />}
                  errorElement={<NotFoundComponent />}
                >
                  {SalesForceRoutes()}
                </Route>
                <Route
                  path='get_started'
                  element={<div>Get started</div>}
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='addons'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyAddonRoutes />
                    </Suspense>
                  }
                  errorElement={<NotFoundComponent />}
                />
                <Route
                  path='settings/*'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazySettingRoutes />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path='access_denied'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyNoAccess />
                    </Suspense>
                  }
                />
                <Route
                  path='token-expired'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyTokenExpiredComponent />
                    </Suspense>
                  }
                />
                <Route
                  path='expired'
                  element={<Navigate to='../access_denied' />}
                />
                <Route
                  path='waitlist'
                  element={<Navigate to='../access_denied' />}
                />
                <Route
                path='save_points'
                element={
                  <Suspense fallback={<PageLoading />}>
                    <LazySavePointsListView />
                  </Suspense>
                }
              />
              <Route
                path='preview/:savePointId'
                element={
                  <Suspense fallback={<PageLoading />}>
                    <LazyPreviewContextProvider>
                      <LazyPreview />
                    </LazyPreviewContextProvider>
                  </Suspense>
                }
              >
                <Route
                  path='cases/filters'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyTestCaseFilter key='cases' />
                    </Suspense>
                  }
                >
                  <Route
                    path=':filterId'
                    element={
                      <Suspense fallback={<PageLoading />}>
                        <LazyListViewContextProvider key='cases'>
                          <LazyListView />
                        </LazyListViewContextProvider>
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path='step-groups/filters'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyTestCaseFilter key='step-groups' />
                    </Suspense>
                  }
                >
                  <Route
                    path=':filterId'
                    element={
                      <Suspense fallback={<PageLoading />}>
                        <LazyListViewContextProvider key='step-groups'>
                          <LazyListView />
                        </LazyListViewContextProvider>
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path='elements/filters'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyElementsFilter />
                    </Suspense>
                  }
                >
                  <Route
                    path=':filterId'
                    element={
                      <RouteGuard
                        access={
                          !userPreference?.preference?.projectPreference
                            ?.selectedVersion?.application?.isRest
                        }
                      >
                        <Suspense fallback={<PageLoading />}>
                          <LazyListViewContextProvider key='elements'>
                            <LazyElementListView />
                          </LazyListViewContextProvider>
                        </Suspense>
                      </RouteGuard>
                    }
                  />
                </Route>
                <Route path='data'>
                  <Route
                    index
                    element={
                      <Suspense fallback={<PageLoading />}>
                        <LazyListViewContextProvider key='testdataProfile'>
                          <LazyDataMapProvider>
                            <LazyTestDataProfileListView />
                          </LazyDataMapProvider>
                        </LazyListViewContextProvider>
                      </Suspense>
                    }
                  />
                  <Route
                    path='filter/:filterId'
                    element={
                      <Suspense fallback={<PageLoading />}>
                        <LazyListViewContextProvider key='testdataProfileWithFilters'>
                          <LazyDataMapProvider>
                            <LazyTestDataProfileListView />
                          </LazyDataMapProvider>
                        </LazyListViewContextProvider>
                      </Suspense>
                    }
                  />
                  <Route
                    path=':dataId/sets/'
                    element={
                      <Suspense fallback={<PageLoading />}>
                        <LazyDetailViewAndFormContextProvider>
                          <LazyTestDataProfileDetailsPage />
                        </LazyDetailViewAndFormContextProvider>
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path='cases/:testCaseId'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyTestcaseSteps key='test-cases' />
                    </Suspense>
                  }
                />
                <Route
                  path='step-groups/:testCaseId'
                  element={
                    <Suspense fallback={<PageLoading />}>
                      <LazyTestcaseSteps key='step-groups' />
                    </Suspense>
                  }
                />
              </Route>
              </Route>
              <Route path='*' element={<NotFoundComponent />} />
            </Routes>
          </ErrorBoundary>
        </div>
      </div>
      <TrailExpired />
      <UserLimitExceeded />
    </>
  );
};
